import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';

const AUTO_PLAY_DELAY = 5000;
const PAUSE_AFTER_CLICK = 10000;

const FeaturesShowcase = () => {
  const [activeTab, setActiveTab] = useState(0);
  const pauseTimerRef = useRef(null);

  const features = [
    {
      id: 0,
      title: "Crystal-Clear Broadcasts",
      subtitle: "HD Video at Any Scale",
      description:
        "DailyRespond guarantees zero-lag 1080p streaming — even for thousands of attendees. Adaptive bitrate tech, no frozen screens, no hassle.",
      icon: "Video",
      image: "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=800&h=500&fit=crop",
      highlights: [
        { icon: "Camera", text: "1080p Full-HD Streaming" },
        { icon: "Cloud", text: "Global CDN Delivery" },
        { icon: "Zap", text: "Adaptive Bitrate" },
        { icon: "Play", text: "1-Click Live/On-Demand" }
      ]
    },
    {
      id: 1,
      title: "Unmatched Audience Engagement",
      subtitle: "Truly Interactive Events",
      description:
        "Polls, quizzes, emoji reactions, and real-time Q&A: make every participant a part of the show. Boost attention, absorption, and feedback scores.",
      icon: "Users",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=500&fit=crop",
      highlights: [
        { icon: "BarChart", text: "Live Polls & Results" },
        { icon: "MessageSquare", text: "Real-Time Chat & Q&A" },
        { icon: "Smile", text: "Emoji Reactions" },
        { icon: "Award", text: "Instant Certificate Download" }
      ]
    },
    {
      id: 2,
      title: "Enterprise-Grade Analytics",
      subtitle: "Actionable Compliance Insights",
      description:
        "Drill down into engagement metrics, session attendance, certificate completion—and automate reporting for audits, HR, and regulatory needs.",
      icon: "BarChart",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop",
      highlights: [
        { icon: "Eye", text: "Live Attendance Tracking" },
        { icon: "Clipboard", text: "Compliance Reporting" },
        { icon: "TrendingUp", text: "ROI & Engagement Metrics" },
        { icon: "Download", text: "1-Click Export" }
      ]
    }
  ];

  const [isPaused, setIsPaused] = useState(false);

  const clearPauseTimer = () => {
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % features.length);
    }, AUTO_PLAY_DELAY);
    return () => clearInterval(interval);
  }, [isPaused, features.length]);

  const handleTabClick = (idx) => {
    setActiveTab(idx);
    setIsPaused(true);
    clearPauseTimer();
    pauseTimerRef.current = setTimeout(() => {
      setIsPaused(false);
    }, PAUSE_AFTER_CLICK);
  };

  useEffect(() => {
    return () => {
      clearPauseTimer();
    };
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-6xl font-black mb-4">
            <span className="text-gray-900">Why DailyRespond?</span>
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Platform Features
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Streamline your compliance, certification, and knowledge delivery—with live interaction, instant reports, and enterprise flexibility.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-gray-100 rounded-2xl p-2 gap-2 relative">
            {features.map((feature, idx) => (
              <motion.button
                key={feature.id}
                onClick={() => handleTabClick(idx)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`relative flex items-center gap-3 px-6 py-4 rounded-xl font-bold transition-all ${activeTab === idx
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Icon name={feature.icon} size={20} />
                <span className="hidden md:inline">{feature.title}</span>
                {activeTab === idx && !isPaused && (
                  <svg className="absolute -top-1 -right-1 w-6 h-6 transform rotate-90">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="rgba(255,255,255,0.3)"
                      strokeWidth="2"
                      fill="none"
                    />
                    <motion.circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="white"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                      initial={{ strokeDasharray: "0 63" }}
                      animate={{ strokeDasharray: "63 63" }}
                      transition={{ duration: AUTO_PLAY_DELAY / 1000, ease: "linear" }}
                      key={`progress-${activeTab}-${isPaused}`}
                    />
                  </svg>
                )}
              </motion.button>
            ))}
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${isPaused ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'}`} />
                <span>{isPaused ? 'Paused' : 'Auto-playing'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            {/* Left: Content */}
            <div className="space-y-6">
              <div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-bold mb-4"
                >
                  {features[activeTab].subtitle}
                </motion.div>
                <motion.h3
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-4xl font-black text-gray-900 mb-4"
                >
                  {features[activeTab].title}
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-lg text-gray-600 leading-relaxed"
                >
                  {features[activeTab].description}
                </motion.p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {features[activeTab].highlights.map((highlight, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
                  >
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name={highlight.icon} size={14} className="text-white" />
                    </div>
                    <span className="text-gray-700 font-semibold text-sm">
                      {highlight.text}
                    </span>
                  </motion.div>
                ))}
              </div>
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-8 py-4 rounded-xl hover:shadow-2xl transition-all inline-flex items-center gap-2"
              >
                Learn More
                <Icon name="ArrowRight" size={20} />
              </motion.button>
            </div>

            {/* Right: 3D Card Image */}
            <motion.div
              initial={{ opacity: 0, rotateY: -30, x: 100 }}
              animate={{ opacity: 1, rotateY: 0, x: 0 }}
              transition={{ duration: 0.6 }}
              className="perspective-1000"
            >
              <motion.div
                whileHover={{
                  rotateY: 5,
                  rotateX: 5,
                  scale: 1.02
                }}
                transition={{ duration: 0.3 }}
                className="relative rounded-3xl overflow-hidden shadow-2xl transform-gpu"
                style={{ transformStyle: 'preserve-3d' }}
              >
                <img
                  src={features[activeTab].image}
                  alt={features[activeTab].title}
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent" />
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="absolute bottom-8 left-8 right-8"
                >
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                        <Icon name={features[activeTab].icon} size={28} className="text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-600">Feature {activeTab + 1} of {features.length}</div>
                        <div className="text-lg font-black text-gray-900">{features[activeTab].title}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* Progress Dots */}
        <div className="flex justify-center gap-3 mt-12">
          {features.map((_, idx) => (
            <button
              key={idx}
              onClick={() => handleTabClick(idx)}
              className={`relative rounded-full transition-all ${activeTab === idx
                ? 'w-12 h-3 bg-gradient-to-r from-blue-600 to-indigo-600'
                : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                }`}
            >
              {activeTab === idx && !isPaused && (
                <motion.div
                  className="absolute inset-0 bg-white/30 rounded-full origin-left"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: AUTO_PLAY_DELAY / 1000, ease: "linear" }}
                  key={`dot-progress-${activeTab}`}
                />
              )}
            </button>
          ))}
        </div>
      </div>
      <style jsx>{`
        .perspective-1000 {
          perspective: 1000px;
        }
      `}</style>
    </section>
  );
};

export default FeaturesShowcase;
